import React, { useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button, Callout, Icon, Tooltip } from '@node-space/ui-components'
import { useSupportedCurrenciesQuery } from 'hooks/queries/useSupportedCurrenciesQuery'
import { CustomerActionIds } from 'types/atoms/atom.customers'
import { customerActionAtom } from './atoms/customersAtoms'
import { CustomerGlobalFeeCard, CustomersSheets, useCustomerFeeSetupForm } from './components'
import { CustomerFeeSetupTypes, feeStructureOptions } from './types'

export const CustomerFeeSetup = () => {
  const { t } = useTranslation()
  const setCustomerAction = useSetAtom(customerActionAtom)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const { data: supportedCurrencies = [] } = useSupportedCurrenciesQuery()

  const currencyOptions = useMemo(() => {
    return supportedCurrencies
      ?.map(currency => ({
        value: currency?.code,
        leftText: currency?.code,
        LeftElement: <Avatar name={currency?.code} size="small" />,
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
  }, [supportedCurrencies])

  const form = useCustomerFeeSetupForm()

  const { formValues, errors, register, setValue } = form

  const handleManageFeeWallets = () => {
    setCustomerAction({
      actionId: CustomerActionIds.ASSIGN_FEE_WALLETS,
      header: t('customers.feeSetup.assignFeeWallets'),
      onClose: () => {
        setIsSheetOpen(false)
      },
    })
    setIsSheetOpen(true)
  }

  const handleManageCustomerFee = () => {
    setCustomerAction({
      actionId: CustomerActionIds.ASSIGN_CUSTOMER_FEE,
      header: t('customers.feeSetup.setCustomerFees'),
      onClose: () => {
        setIsSheetOpen(false)
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <Box>
      <Box flex width="full" justify="between" align="center">
        <p className="text-sm text-foreground-subdued leading-5">
          {t('customers.feeSetup.description')}
        </p>
        <Button
          variant="secondary"
          iconPosition="left"
          iconElement={<Icon name="Wallet" />}
          onClick={handleManageFeeWallets}
        >
          {t('customers.feeSetup.manageFeeWallets')}
        </Button>
      </Box>

      <Box flexCol gapY={16} width="full" paddingT={20}>
        <Box flexCol gapY={8} width="full">
          <h3 className="text-base font-medium text-foreground-prominent leading-[22px]">
            {t('customers.feeSetup.transactionFees')}
          </h3>
          <Callout variant="info">{t('customers.feeSetup.transactionFeesCallout')}</Callout>
        </Box>
        <Box flexCol gapY={8} width="full">
          <Box flex align="center">
            <h3 className="text-base font-medium text-foreground-prominent leading-[22px]">
              {t('customers.feeSetup.customerSpecificFees')}
            </h3>
            <Tooltip
              bodyContent={t('customers.feeSetup.customerSpecificFeesOverride')}
              type="simple"
              position="bottom"
            >
              <Button variant="ghost" iconElement={<Icon name="Info" />} />
            </Tooltip>
          </Box>
          <Box className="bg-surface-interactive border border-stroke-interactive rounded-lg p-4">
            <Button variant="primary" onClick={handleManageCustomerFee}>
              {t('customers.feeSetup.addFee')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box flexCol gapY={16} width="full" paddingT={28}>
        <Box flexCol gapY={8}>
          <h3 className="text-base font-medium text-foreground-prominent leading-[22px]">
            {t('customers.feeSetup.globalFees')}
          </h3>
        </Box>

        <Box flex gapX={24} width="full">
          <CustomerGlobalFeeCard
            type={CustomerFeeSetupTypes.PAYINS}
            customerFeePrefix="payins"
            currencyOptions={currencyOptions}
            feeStructureOptions={feeStructureOptions}
            formValues={formValues}
            errors={errors}
            register={register}
            setValue={setValue}
          />

          <CustomerGlobalFeeCard
            type={CustomerFeeSetupTypes.PAYOUTS}
            customerFeePrefix="payouts"
            currencyOptions={currencyOptions}
            feeStructureOptions={feeStructureOptions}
            formValues={formValues}
            errors={errors}
            register={register}
            setValue={setValue}
          />
        </Box>
      </Box>

      <CustomersSheets isSheetOpen={isSheetOpen} />
    </Box>
  )
}
